import { FC, useCallback, useState, useEffect } from 'react'

import { MIN_ADULTS, MIN_CHILDREN, MIN_PETS } from './constants'
import GuestCountersContainer from './guest-counters-container'
import { GuestPickerExclusiveProps, GuestPickerPassThroughProps } from './types'

type Props = GuestPickerPassThroughProps & {
    component: GuestPickerExclusiveProps['modalComponent']
}

const Modal: FC<Props> = ({
    component: Component,
    isOpen,
    onOpen,
    onClose,
    adultsCount: initialAdultsCount,
    childrenCount: initialChildrenCount,
    petsCount: initialPetsCount,
    onAdultsCountChange,
    onChildrenCountChange,
    onPetsCountChange,
    petsAllowed,
    ...restProps
}) => {
    const { minAdults, minChildren, minPets } = restProps

    const [adultsCount, setAdultsCount] = useState(initialAdultsCount)
    const [childrenCount, setChildrenCount] = useState(initialChildrenCount)
    const [petsCount, setPetsCount] = useState(initialPetsCount)

    // Update state when initialAdultsCount or initialChildrenCount or initialPetsCount change
    useEffect(() => {
        setAdultsCount(initialAdultsCount)
    }, [initialAdultsCount])

    useEffect(() => {
        setChildrenCount(initialChildrenCount)
    }, [initialChildrenCount])

    useEffect(() => {
        setPetsCount(initialPetsCount)
    }, [initialPetsCount])

    const _handleClear = useCallback(() => {
        setAdultsCount(minAdults ?? MIN_ADULTS)
        setChildrenCount(minChildren ?? MIN_CHILDREN)
        if (petsAllowed) {
            setPetsCount(minPets ?? MIN_PETS)
        }
    }, [minAdults, minChildren, minPets])

    const _handleReset = useCallback(() => {
        setAdultsCount(initialAdultsCount)
        setChildrenCount(initialChildrenCount)
        if (petsAllowed) {
            setPetsCount(initialPetsCount)
        }
    }, [initialAdultsCount, initialChildrenCount, initialPetsCount])

    const _handleSubmit = useCallback(() => {
        onAdultsCountChange(adultsCount)
        onChildrenCountChange(childrenCount)
        if (petsAllowed) {
            onPetsCountChange?.(petsCount ?? MIN_PETS)
        }
    }, [adultsCount, childrenCount, petsCount, onAdultsCountChange, onChildrenCountChange, onPetsCountChange])

    return (
        <Component
            {...restProps}
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            onClear={_handleClear}
            onReset={_handleReset}
            onSubmit={_handleSubmit}
            adultsCount={adultsCount}
            childrenCount={childrenCount}
            petsCount={petsCount}
            onAdultsCountChange={setAdultsCount}
            onChildrenCountChange={setChildrenCount}
            onPetsCountChange={setPetsCount}
            petsAllowed={petsAllowed}
        >
            <GuestCountersContainer
                {...restProps}
                adultsCount={adultsCount}
                childrenCount={childrenCount}
                petsCount={petsCount}
                onAdultsCountChange={setAdultsCount}
                onChildrenCountChange={setChildrenCount}
                onPetsCountChange={setPetsCount}
                petsAllowed={petsAllowed}
            />
        </Component>
    )
}

export default Modal
